import React, { useState, useRef } from "react";
import { Box } from "@mui/material";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import SortableDockItem from "./SortableDockItem";

export type DockPosition = "bottom" | "left" | "right";

export interface DockItemData {
  id: string;
  label: string;
  icon: React.ReactNode;
  isRunning?: boolean;
  onClick?: () => void;
}

interface DockProps {
  items: DockItemData[];
  defaultIconSize?: number;
  maxScale?: number;
  proximityRange?: number;
  position?: DockPosition;
  onReorder?: (items: DockItemData[]) => void;
}

const Dock: React.FC<DockProps> = ({
  items,
  defaultIconSize = 48,
  maxScale = 1.5,
  proximityRange = 2,
  position = "bottom",
  onReorder,
}) => {
  const [mousePosition, setMousePosition] = useState<number | null>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const isVertical = position === "left" || position === "right";

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      const newItems = arrayMove(items, oldIndex, newIndex);
      onReorder?.(newItems);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dockRef.current) {
      const rect = dockRef.current.getBoundingClientRect();
      if (isVertical) {
        setMousePosition(e.clientY - rect.top);
      } else {
        setMousePosition(e.clientX - rect.left);
      }
    }
  };

  const handleMouseLeave = () => {
    setMousePosition(null);
  };

  const calculateScale = (index: number): number => {
    if (mousePosition === null || !itemRefs.current[index]) {
      return 1.0;
    }

    const itemElement = itemRefs.current[index];
    if (!itemElement) return 1.0;

    const rect = itemElement.getBoundingClientRect();
    const dockRect = dockRef.current?.getBoundingClientRect();
    if (!dockRect) return 1.0;

    let itemCenter: number;
    if (isVertical) {
      itemCenter = rect.top - dockRect.top + rect.height / 2;
    } else {
      itemCenter = rect.left - dockRect.left + rect.width / 2;
    }

    const distance = Math.abs(mousePosition - itemCenter);

    // Calculate proximity (0 = far, 1 = directly over)
    const maxDistance = defaultIconSize * proximityRange;
    const proximity = Math.max(0, 1 - distance / maxDistance);

    // Interpolate scale based on proximity
    const scale = 1.0 + proximity * (maxScale - 1.0);

    return scale;
  };

  const getPositionStyles = () => {
    const baseStyles = {
      position: "fixed" as const,
      display: "flex",
      gap: 1,
      background: (theme: any) =>
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(255, 255, 255, 0.7)",
      backdropFilter: "blur(20px)",
      borderRadius: "16px",
      border: (theme: any) =>
        theme.palette.mode === "dark"
          ? "1px solid rgba(255, 255, 255, 0.2)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      boxShadow: (theme: any) =>
        theme.palette.mode === "dark"
          ? "0 8px 32px rgba(0, 0, 0, 0.5)"
          : "0 8px 32px rgba(0, 0, 0, 0.15)",
      zIndex: 1000,
    };

    switch (position) {
      case "left":
        return {
          ...baseStyles,
          left: 16,
          top: "50%",
          transform: "translateY(-50%)",
          maxHeight: "calc(100vh - 64px - 32px)", // viewport - header - margins
          flexDirection: "column" as const,
          alignItems: "center" as const,
          padding: "16px 12px",
          overflowY: "auto" as const,
          overflowX: "hidden" as const,
          // Hide scrollbar but keep functionality
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: (theme: any) =>
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.3)"
                : "rgba(0, 0, 0, 0.3)",
            borderRadius: "2px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: (theme: any) =>
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.5)"
                : "rgba(0, 0, 0, 0.5)",
          },
        };
      case "right":
        return {
          ...baseStyles,
          right: 16,
          top: "50%",
          transform: "translateY(-50%)",
          maxHeight: "calc(100vh - 128px - 24px)", // viewport - header - margins
          flexDirection: "column" as const,
          alignItems: "center" as const,
          padding: "16px 12px",
          overflowY: "auto" as const,
          overflowX: "hidden" as const,
          // Hide scrollbar but keep functionality
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: (theme: any) =>
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.3)"
                : "rgba(0, 0, 0, 0.3)",
            borderRadius: "2px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: (theme: any) =>
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.5)"
                : "rgba(0, 0, 0, 0.5)",
          },
        };
      case "bottom":
      default:
        return {
          ...baseStyles,
          bottom: 16,
          left: "50%",
          transform: "translateX(-50%)",
          flexDirection: "row" as const,
          alignItems: "flex-end" as const,
          padding: "12px 16px",
          maxWidth: "calc(100vw - 32px)",
          overflowX: "auto" as const,
          overflowY: "hidden" as const,
          // Hide scrollbar but keep functionality
          "&::-webkit-scrollbar": {
            height: "4px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: (theme: any) =>
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.3)"
                : "rgba(0, 0, 0, 0.3)",
            borderRadius: "2px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: (theme: any) =>
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.5)"
                : "rgba(0, 0, 0, 0.5)",
          },
        };
    }
  };

  return (
    <Box
      ref={dockRef}
      data-disco="dock"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      sx={getPositionStyles()}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={
            isVertical
              ? verticalListSortingStrategy
              : horizontalListSortingStrategy
          }
        >
          {items.map((item, index) => (
            <SortableDockItem
              key={item.id}
              item={item}
              scale={calculateScale(index)}
              defaultSize={defaultIconSize}
              isVertical={isVertical}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export default Dock;
